import React, { useState, useEffect } from 'react';
import {
    Container,
    Typography,
    Paper,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    FormControlLabel,
    Switch,
    Box,
    IconButton,
    Tooltip,
    Alert,
    Snackbar,
    Input
} from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon, Upload } from '@mui/icons-material';
import { spreadsService } from '../../services/spreadsService';
import { storageService } from '../../services/storageService';
import { Spread } from '../../types/spread';

interface SpreadFormData {
    title: string;
    description: string;
    deckCount: number;
    readingDuration: string;
    price: number;
    isActive: boolean;
    order: number;
    imageUrl: string;
    points: number;
}

const initialFormData: SpreadFormData = {
    title: '',
    description: '',
    deckCount: 1,
    readingDuration: '',
    price: 0,
    isActive: true,
    order: 0,
    imageUrl: '',
    points: 0
};

const SpreadsAdmin: React.FC = () => {
    const [spreads, setSpreads] = useState<Spread[]>([]);
    const [loading, setLoading] = useState(true);
    const [openDialog, setOpenDialog] = useState(false);
    const [formData, setFormData] = useState<SpreadFormData>(initialFormData);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
        open: false,
        message: '',
        severity: 'success'
    });
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [uploadLoading, setUploadLoading] = useState(false);

    useEffect(() => {
        loadSpreads();
    }, []);

    const loadSpreads = async () => {
        try {
            setLoading(true);
            const spreadsData = await spreadsService.getAll();
            setSpreads(spreadsData);
        } catch (error) {
            console.error('Error loading spreads:', error);
            showSnackbar('Fallar yüklenirken bir hata oluştu', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleOpenDialog = (spread?: Spread) => {
        if (spread) {
            setFormData({
                title: spread.title,
                description: spread.description,
                deckCount: spread.deckCount,
                readingDuration: spread.readingDuration,
                price: spread.price,
                isActive: spread.isActive,
                order: spread.order,
                imageUrl: spread.imageUrl,
                points: spread.points || 0
            });
            setEditingId(spread.id);
        } else {
            setFormData(initialFormData);
            setEditingId(null);
        }
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setFormData(initialFormData);
        setEditingId(null);
    };

    const handleSubmit = async () => {
        try {
            // Form validasyonu
            if (!formData.title || !formData.description || !formData.readingDuration || formData.price <= 0) {
                showSnackbar('Lütfen tüm zorunlu alanları doldurun', 'error');
                return;
            }

            // Spread verisi hazırla
            const spreadData = {
                title: formData.title.trim(),
                description: formData.description.trim(),
                deckCount: formData.deckCount,
                readingDuration: formData.readingDuration.trim(),
                price: formData.price,
                isActive: formData.isActive,
                order: formData.order || spreads.length, // Eğer order belirtilmemişse son sıraya ekle
                imageUrl: formData.imageUrl.trim() || spreadsService.getNextImage(),
                points: formData.points || 0
            };

            if (editingId) {
                await spreadsService.update(editingId, spreadData);
                showSnackbar('Fal başarıyla güncellendi', 'success');
            } else {
                const newSpread = await spreadsService.create(spreadData);
                console.log('Created spread:', newSpread); // Debug için
                if (!newSpread.id) {
                    throw new Error('Spread ID was not generated');
                }
                showSnackbar('Fal başarıyla oluşturuldu', 'success');
            }
            
            handleCloseDialog();
            await loadSpreads();
        } catch (error) {
            console.error('Error saving spread:', error);
            showSnackbar(
                error instanceof Error ? error.message : 'Fal kaydedilirken bir hata oluştu',
                'error'
            );
        }
    };

    const handleDelete = async (id: string) => {
        if (window.confirm('Bu falı silmek istediğinizden emin misiniz?')) {
            try {
                await spreadsService.delete(id);
                showSnackbar('Fal başarıyla silindi', 'success');
                loadSpreads();
            } catch (error) {
                console.error('Error deleting spread:', error);
                showSnackbar('Fal silinirken bir hata oluştu', 'error');
            }
        }
    };

    const showSnackbar = (message: string, severity: 'success' | 'error') => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar({ ...snackbar, open: false });
    };

    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (file) {
            setSelectedFile(file);
        }
    };

    const handleUploadImage = async () => {
        if (!selectedFile) return;

        try {
            setUploadLoading(true);
            const imageUrl = await storageService.uploadSpreadImage(selectedFile);
            setFormData(prev => ({ ...prev, imageUrl }));
            showSnackbar('Resim başarıyla yüklendi', 'success');
            setSelectedFile(null);
        } catch (error) {
            console.error('Error uploading image:', error);
            showSnackbar(
                error instanceof Error ? error.message : 'Resim yüklenirken bir hata oluştu',
                'error'
            );
        } finally {
            setUploadLoading(false);
        }
    };

    const columns: GridColDef[] = [
        { field: 'title', headerName: 'Fal Adı', width: 200 },
        { field: 'description', headerName: 'Açıklama', width: 300 },
        { field: 'deckCount', headerName: 'Deste Sayısı', width: 120 },
        { 
            field: 'price', 
            headerName: 'Ücret', 
            width: 120,
            renderCell: (params) => `${params.value} ₺`
        },
        { 
            field: 'points', 
            headerName: 'Puan', 
            width: 120,
            renderCell: (params) => params.value || 0
        },
        { 
            field: 'readingDuration', 
            headerName: 'Soru Hakkı', 
            width: 120
        },
        { 
            field: 'isActive', 
            headerName: 'Durum', 
            width: 120,
            renderCell: (params) => params.value ? 'Aktif' : 'Pasif'
        },
        {
            field: 'actions',
            headerName: 'İşlemler',
            width: 120,
            renderCell: (params: GridRenderCellParams) => (
                <Box>
                    <Tooltip title="Düzenle">
                        <IconButton onClick={() => handleOpenDialog(params.row)}>
                            <EditIcon />
                        </IconButton>
                    </Tooltip>
                    <Tooltip title="Sil">
                        <IconButton onClick={() => handleDelete(params.row.id)} color="error">
                            <DeleteIcon />
                        </IconButton>
                    </Tooltip>
                </Box>
            )
        }
    ];

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h4" component="h1">
                    Fallar
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<AddIcon />}
                    onClick={() => handleOpenDialog()}
                >
                    Yeni Fal Ekle
                </Button>
            </Box>

            <Paper sx={{ width: '100%', overflow: 'hidden' }}>
                <DataGrid
                    rows={spreads}
                    columns={columns}
                    loading={loading}
                    autoHeight
                    pageSizeOptions={[10, 25, 50]}
                    initialState={{
                        pagination: {
                            paginationModel: {
                                pageSize: 10,
                            },
                        },
                    }}
                    disableRowSelectionOnClick
                />
            </Paper>

            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingId ? 'Fal Düzenle' : 'Yeni Fal'}
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        <TextField
                            fullWidth
                            label="Fal Adı"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Açıklama"
                            value={formData.description}
                            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            margin="normal"
                            multiline
                            rows={3}
                        />
                        <TextField
                            fullWidth
                            label="Deste Sayısı"
                            type="number"
                            value={formData.deckCount}
                            onChange={(e) => setFormData({ ...formData, deckCount: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Soru Hakkı"
                            value={formData.readingDuration}
                            onChange={(e) => setFormData({ ...formData, readingDuration: e.target.value })}
                            margin="normal"
                            placeholder="Örn: 30 dakika"
                        />
                        <TextField
                            fullWidth
                            label="Ücret (₺)"
                            type="number"
                            value={formData.price}
                            onChange={(e) => setFormData({ ...formData, price: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <TextField
                            fullWidth
                            label="Kazanılacak Puan"
                            type="number"
                            value={formData.points}
                            onChange={(e) => setFormData({ ...formData, points: parseInt(e.target.value) || 0 })}
                            margin="normal"
                            helperText="Bu falı yaptıran kullanıcılar bu kadar puan kazanacak"
                        />
                        <TextField
                            fullWidth
                            label="Sıralama"
                            type="number"
                            value={formData.order}
                            onChange={(e) => setFormData({ ...formData, order: parseInt(e.target.value) || 0 })}
                            margin="normal"
                        />
                        <Box sx={{ mt: 2, mb: 2 }}>
                            <input
                                accept="image/*"
                                style={{ display: 'none' }}
                                id="spread-image-upload"
                                type="file"
                                onChange={handleFileSelect}
                            />
                            <label htmlFor="spread-image-upload">
                                <Button
                                    variant="outlined"
                                    component="span"
                                    startIcon={<Upload />}
                                    disabled={uploadLoading}
                                >
                                    Resim Seç
                                </Button>
                            </label>
                            {selectedFile && (
                                <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
                                    <Typography variant="body2">
                                        {selectedFile.name}
                                    </Typography>
                                    <Button
                                        variant="contained"
                                        onClick={handleUploadImage}
                                        disabled={uploadLoading}
                                        size="small"
                                    >
                                        {uploadLoading ? 'Yükleniyor...' : 'Yükle'}
                                    </Button>
                                </Box>
                            )}
                        </Box>
                        <TextField
                            fullWidth
                            label="Resim URL"
                            value={formData.imageUrl}
                            onChange={(e) => setFormData(prev => ({ ...prev, imageUrl: e.target.value }))}
                            margin="normal"
                            disabled={uploadLoading}
                        />
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={formData.isActive}
                                    onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                                />
                            }
                            label="Aktif"
                            sx={{ mt: 2 }}
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>İptal</Button>
                    <Button variant="contained" color="primary" onClick={handleSubmit}>
                        Kaydet
                    </Button>
                </DialogActions>
            </Dialog>

            <Snackbar
                open={snackbar.open}
                autoHideDuration={6000}
                onClose={handleCloseSnackbar}
                message={snackbar.message}
            />
        </Container>
    );
};

export default SpreadsAdmin; 